import React, { FC } from 'react';
import { useFormik } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';

import { useToastErrorMessage } from '@/utils/Messages';

import { putGuest } from '@/services/Guest/request';
import { Property, PropertyGrouped } from '@/services/Property/types';

import { useEditForm } from '@/context/EditFormContext';
import { useLoader } from '@/context/LoaderContext';
import { useReservation } from '@/context/ReservationContext';
import { useToast } from '@/context/ToastContext';

import { useReservationForm } from '@/hooks/ReservationHook/useReservationForm';

import FormGridRow from '@/components/FormGridRow';
import {
  DatePicker,
  FormButton,
  RadioGender,
  RadioTypePerson,
  TextField,
} from '@/components/index';

import { ButtonsContainer, FormContainer, FormContent } from './styles';

type UserReservation = {
  first_name: string;
  last_name: string;
  cpf: string;
  cnpj: string;
  idNumber: string;
  email: string;
  phone: string;
  cep: string;
  street: string;
  number: number;
  neigbhood: string;
  city: string;
  gender: string;
  isIndividual: string;
  birth_date: string;
};

type GuestReservation = {
  user: UserReservation;
};

type GeneralReservation = {
  guest: GuestReservation;
};
interface Props {
  reservation: GeneralReservation;
  activeButton: (a: boolean) => void;
}

const PersonalDataForm: FC<Props> = ({ reservation, activeButton }) => {
  const { reservationData, setReservationData } = useReservation();
  const propertyId = reservationData?.listing?.property?.id;
  const { dataGrouped, setDataGrouped } = useReservationForm();
  const toast = useToast();
  const { setLoad } = useLoader();
  const toastErrorRequest = useToastErrorMessage();
  const { setOpenForm, setFormActive } = useEditForm();

  const handleCloseEditForm = () => {
    setOpenForm(false);
    setFormActive(0);
    activeButton(false);
  };

  function replaceAll(string: string, search: string, replace: string) {
    return string.split(search).join(replace);
  }

  const validation = Yup.object().shape({
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    gender: Yup.string().required(),
    typePerson: Yup.string().required(),
    born: Yup.string(),
    idNumber: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      firstName: reservation.guest.user.first_name,
      lastName: reservation.guest.user.last_name,
      gender: reservation.guest.user.gender,
      typePerson: reservation.guest.user.isIndividual ? 'individual' : 'legal',
      cpf: reservation.guest.user.cpf,
      cnpj: reservation.guest.user.cnpj,
      idNumber: reservation.guest.user.idNumber,
      born: new Date(moment(reservation.guest.user.birth_date).format('YYYY-MM-DD')),
    },
    validationSchema: validation,
    onSubmit: async (values) => {
      setLoad(true);
      try {
        const guest = {
          ...reservationData?.guest,
          user: {
            email: reservationData?.guest.user.email,
            corporate_name: reservationData?.guest.user.corporate_name,
            trading_name: reservationData.guest.user.trading_name,
            first_name: values.firstName,
            last_name: values.lastName,
            gender: values.gender,
            is_individual: values.typePerson === 'individual',
            birth_date: moment(values.born).format('DD/MM/YYYY'),
            cpf: replaceAll(replaceAll(values.cpf, '.', ''), '-', ''),
            id_number: values.idNumber,
            cnpj: values.cnpj,
            main_role: 'Guest',
            main_address: reservationData.guest.user.main_address,
          },
        };
        if (
          guest.user.cpf === reservationData.guest.user.cpf ||
          values.typePerson !== 'individual'
        ) {
          delete guest.user.cpf;
        }
        if (
          values.typePerson === 'individual' ||
          reservationData.guest.user.cnpj === guest.user.cnpj
        ) {
          delete guest.user.cnpj;
        }
        await putGuest(guest.id, guest);

        guest.user.cpf = reservationData.guest.user.cpf;
        guest.user.cnpj = reservationData.guest.user.cnpj;
        guest.user.id = reservationData.guest.user.id;
        const newReservation = {
          ...reservationData,
          guest,
        };

        await setReservationData({ ...newReservation });
        const key = newReservation?.check_in_date;

        if (dataGrouped) {
          setDataGrouped(
            dataGrouped.map((category: PropertyGrouped) => ({
              ...category,
              data: category.data.map<Property>((property) => ({
                ...property,
                reservation:
                  property?.id === propertyId
                    ? { ...property.reservation, [key]: { ...newReservation } }
                    : { ...property.reservation },
              })),
            })),
          );
        }
        toast.success('Os dados pessoais foram salvos');
        handleCloseEditForm();
        setLoad(false);
      } catch (e: unknown) {
        if (e instanceof Error) {
          setLoad(false);
          toastErrorRequest(e);
        }
      }
    },
  });

  return (
    <FormContainer
      onContextMenu={(event): void => {
        event.stopPropagation();
      }}
    >
      <FormContent onSubmit={formik.handleSubmit}>
        <FormGridRow>
          <TextField
            label="Primeiro nome"
            formik={formik}
            id="firstName"
            value={reservation.guest.user.first_name}
          />
        </FormGridRow>
        <FormGridRow>
          <TextField
            label="Restante do nome"
            formik={formik}
            id="lastName"
            value={reservation.guest.user.last_name}
          />
        </FormGridRow>

        <FormGridRow>
          <RadioGender labelPlacement="end" id="gender" formik={formik} />
        </FormGridRow>

        <FormGridRow>
          <DatePicker
            formik={formik}
            label="*Data de nascimento"
            id="born"
            minDate={new Date('1850-01-02')}
          />
        </FormGridRow>

        <FormGridRow>
          <RadioTypePerson labelPlacement="end" id="typePerson" formik={formik} />
        </FormGridRow>

        {formik.values.typePerson === 'individual' ? (
          <>
            <FormGridRow>
              <TextField
                label="*CPF"
                formik={formik}
                id="cpf"
                mask="cpf"
                value={reservation.guest.user.cpf}
              />
            </FormGridRow>
            <FormGridRow>
              <TextField
                label="Nº de identidade"
                formik={formik}
                id="idNumber"
                value={reservation.guest.user.idNumber}
              />
            </FormGridRow>
          </>
        ) : (
          <>
            <FormGridRow>
              <TextField
                label="*CNPJ"
                formik={formik}
                id="cnpj"
                mask="cnpj"
                value={reservation.guest.user.cnpj}
              />
            </FormGridRow>
            <FormGridRow>
              <TextField label="Razão social" formik={formik} id="razao" />
            </FormGridRow>
          </>
        )}

        <ButtonsContainer>
          <FormButton type="button" customColor="grey" onClick={() => handleCloseEditForm()}>
            Cancelar
          </FormButton>
          <FormButton onClick={() => formik.handleSubmit}>Salvar</FormButton>
        </ButtonsContainer>
      </FormContent>
    </FormContainer>
  );
};

export default PersonalDataForm;
